import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import {
  COVIE_APPLICATION_UPLOAD,
  CovieApplicationUploadResponse,
} from 'src/configs/APISchemas/CovieApplicationUploadGraphQL';
import { ROUTES } from 'src/constants/routes';
import { logToBackend } from 'src/helpers/BackendLogger';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import { useLocationSearch } from 'src/helpers/hooks/useLocationSearch';
import Container from '../../components/Container';
import { updateCovieAction } from '../../redux/actions/covie';
import { updateComplianceProviderAction } from '../../redux/actions/verifyPolicy';
import { getUtmSource } from '../../redux/selectors/system';
import {
  getVerifyPolicyComplianceProvider,
  getVerifyPolicyInput,
} from '../../redux/selectors/verifyPolicy';
import CovieWidget from './components/CovieWidget';
import { CovieSuccessData } from './types';
import { displayErrorToast } from './utils/display-error-toast.util';
import { generateCovieMetadata } from './utils/generate-covie-metadata.util';
import { validateVerifyPolicyInputData } from './utils/validate-verify-policy-input-data.util';

const ComplianceProvider = () => {
  const getUrlWithQueryParams = useLocationSearch();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const complianceProviderData = useSelector(getVerifyPolicyComplianceProvider);
  const verifyPolicyInput = useSelector(getVerifyPolicyInput);
  const utmSource = useSelector(getUtmSource);

  const [localActiveCovieLink, setLocalActiveCovieLink] = useState<
    string | null
  >(complianceProviderData?.activeCovieLink ?? null);
  const isValidQuoteData = useMemo(
    () => validateVerifyPolicyInputData(verifyPolicyInput),
    [verifyPolicyInput],
  );
  useEffect(() => {
    if (!localActiveCovieLink) {
      return;
    }

    dispatch(
      updateComplianceProviderAction({
        activeCovieLink: localActiveCovieLink,
      }),
    );
  }, [dispatch, localActiveCovieLink]);

  const covieMetadata = useMemo(
    () =>
      generateCovieMetadata({
        isValidVerifyPolicyInputData: isValidQuoteData,
        verifyPolicyInputData: verifyPolicyInput,
      }),
    [isValidQuoteData, verifyPolicyInput],
  );

  const onCovieInit = (linkId: string) => {
    setLocalActiveCovieLink(linkId);
  };

  const onCovieError = async (linkId: string, error: unknown) => {
    const errorMessage = `${linkId}: ${error}`;

    await logToBackend(errorMessage);

    navigate(getUrlWithQueryParams(ROUTES.COMPLIANCE_NO_POLICY_PATH));
  };

  const onCovieDocumentUpload = async (documentLink: string) => {
    if (!verifyPolicyInput || !isValidQuoteData) {
      return;
    }

    const { firstName, lastName } = verifyPolicyInput;
    const fullName = `${firstName} ${lastName}`;
    const address: Parameters<typeof COVIE_APPLICATION_UPLOAD>[4] = {
      street: verifyPolicyInput.address.street,
      addressNumber: verifyPolicyInput.address.addressNumber,
      postalCode: verifyPolicyInput.address.postalCode,
      municipality: verifyPolicyInput.address.municipality,
      region: verifyPolicyInput.address.state,
    };

    const uploadCovieApplicationMutation = COVIE_APPLICATION_UPLOAD(
      fullName,
      firstName,
      lastName,
      verifyPolicyInput.email,
      address,
      verifyPolicyInput.buildingUnit,
      documentLink,
      utmSource,
    );

    const uploadCovieApplicationResponse =
      await GraphQLRequest<CovieApplicationUploadResponse>(
        uploadCovieApplicationMutation,
      );
    const applicationUploadId =
      uploadCovieApplicationResponse?.applicationUpload?.id;

    if (!applicationUploadId) {
      displayErrorToast('Unknown response');
      return;
    }

    navigate(getUrlWithQueryParams(ROUTES.COMPLIANCE_UPLOAD_SUCCESS_PATH));
  };

  const onCovieSuccess = (data: CovieSuccessData) => {
    dispatch(
      updateCovieAction({
        carrier: data.carrier,
        inception_date: data.inception_date,
      }),
    );

    const route = generatePath(ROUTES.COMPLIANCE_COVIE_PATH, {
      covie_id: data.id,
    });
    navigate(route);
  };

  return (
    <Container>
      <Box sx={complianceProviderStyles.root}>
        <Box sx={complianceProviderStyles.container}>
          <Box sx={complianceProviderStyles.covieWrapper}>
            <CovieWidget
              metaData={covieMetadata}
              activeCovieLink={localActiveCovieLink}
              onInit={onCovieInit}
              onDocumentUpload={onCovieDocumentUpload}
              onError={onCovieError}
              onSuccess={onCovieSuccess}
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

const complianceProviderStyles = {
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    flexDirection: 'row',
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    px: {
      xs: 5,
      xl: 30,
    },
    width: '100%',
    maxWidth: 1200,
  },
  header: {
    mb: '40px',
  },
  covieWrapper: {
    border: '1px solid #f5f5f5',
    borderBottom: 'none',
  },
};

export default ComplianceProvider;
